import { useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { ISkipReason } from '../../viewModels/queue';
import { queryKeys } from './queryKeys';

export type UseSkipReasonsResult = {
	/**
	 * "Permanent" skip reasons — `excludesFromPool === true`. Mirrors legacy
	 * `QueueViewModel.skipReasons` (`viewModels/queue.ts:413`). These reasons
	 * remove the lead from the pool entirely (e.g. `BadLead`, `Disqualified`).
	 */
	skipReasons: ISkipReason[];
	/**
	 * "Temporary" skip reasons — `excludesFromPool !== true`. Mirrors legacy
	 * `QueueViewModel.tempSkipReasons` (`viewModels/queue.ts:415`). The lead
	 * stays in the pool and re-surfaces later (e.g. `LeftMessage`,
	 * `WrongNumber` per-tenant configuration).
	 */
	tempSkipReasons: ISkipReason[];
	isLoading: boolean;
	error: Error | null;
};

/**
 * Fetches the per-lead skip reasons via `GET lead/{companyId}/SkipReason`.
 *
 * Mirrors `QueueViewModel.loadSkipReasons` (`aida/viewModels/queue.ts:405-421`)
 * — partitions the response by `excludesFromPool` and exposes the two buckets
 * separately so the picker can render them under their legacy headings
 * ("Skip" vs. "Temporarily skip").
 *
 * Disabled when `companyId` is missing (the focused view's TopBar gates the
 * skip affordance on a loaded lead anyway, but the hook stays defensive).
 */
export function useSkipReasons(companyId: string | undefined): UseSkipReasonsResult {
	const userSession = useUserSession();

	const query = useQuery<{ skipReasons: ISkipReason[]; tempSkipReasons: ISkipReason[] }, Error>({
		queryKey: queryKeys.focusedView.skipReasons(companyId),
		enabled: !!companyId,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<ISkipReason[]>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/SkipReason` }),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}

			const all = result.value ?? [];
			return {
				skipReasons: all.filter(r => r.excludesFromPool === true),
				tempSkipReasons: all.filter(r => r.excludesFromPool !== true),
			};
		},
	});

	return {
		skipReasons: query.data?.skipReasons ?? [],
		tempSkipReasons: query.data?.tempSkipReasons ?? [],
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
